import { useCallback, useEffect, useRef } from 'react'
import type { useToast, EditorCover } from '@papan/core-ui/ui'
import { useTranslation } from '@papan/i18n'

type ToastApi = ReturnType<typeof useToast>

/**
 * The message shown when a version preview — or an editor's own cover (#930, `EditorCover`) — refuses a save.
 *
 * Written once because both entry points — the workspace and the single-file
 * view — have to say the same thing. They had a copy each for a round and the
 * copies had already drifted.
 *
 * Three rules, each one a bug that reached review:
 *
 *   - SAY SOMETHING. A swallowed keystroke gives no sign it was refused.
 *   - ONLY WHEN THERE IS WORK — the CALLER decides, via `hasUnsavedWork`.
 *     "Close the preview to save" over a file with nothing unsaved implies
 *     something is waiting, and Cmd+S out of habit over a saved file is the
 *     common case.
 *   - ONE ON SCREEN, AND NOT PAST ITS SUBJECT. Nothing dedupes toasts, so key
 *     repeat filled the screen to the library's cap, and dismissing one left
 *     the rest demanding a preview that was already shut.
 *
 * ACCEPTED, not fixed (Season, 2026-10-02): "one on screen" is less than "one
 * toast". `removeToast` only hides — react-hot-toast keeps the node about a
 * second — and each call mints a new id, so a screen reader hears the sentence
 * once per keypress inside that window. The fix is an optional caller-supplied
 * id on `addToast`, which is `shared/core-ui` and every toast in the app.
 */
/**
 * `blocked` is WHAT refuses the save — `'preview'`, the cover object itself (one per report), or null: the notice is
 * cleared whenever that changes (by identity), not only when nothing covers any more, so it never outlives its subject
 * while another cover stays up (a preview closed under a conflict panel, another covered tab, a cover replaced by
 * another). A boolean is the one-subject form.
 */
/**
 * What a version preview says when it refuses a save — the Cmd+S toast and the Save As dialog alike: ONE key,
 * `collab:versionPreview.saveBlocked` (its action `versionPreview.close`), in every locale (D3, 2026-10-20 — it was
 * an English literal while the conflict panel's notice was translated).
 */
export const PREVIEW_SAVE_BLOCKED_KEY = 'versionPreview.saveBlocked'

export function useSaveBlockedNotice(toast: ToastApi, blocked: 'preview' | EditorCover | boolean | null) {
  const { t } = useTranslation('collab')
  const idRef = useRef<string | null>(null)

  const clear = useCallback(() => {
    const id = idRef.current
    if (!id) return
    idRef.current = null
    toast.removeToast(id)
  }, [toast])

  const subjectRef = useRef(blocked)
  useEffect(() => {
    if (subjectRef.current === blocked) return
    subjectRef.current = blocked
    clear()
  }, [blocked, clear])

  // Also on unmount: after one, the action closes over a dead tree and does
  // nothing, while the library still dismisses the card — so it reads as
  // success. Being HIDDEN is not an unmount and is not covered; nothing is
  // claimed here about switching file in place (three attempts, all wrong).
  useEffect(() => clear, [clear])

  /** `cover` (#930): an editor's own cover says what to show (its message, its way back); absent = a version preview. */
  return useCallback((hasUnsavedWork: boolean, onClosePreview: () => void, cover?: { message: string; actionLabel: string }) => {
    if (!hasUnsavedWork) return
    clear()
    idRef.current = toast.info(cover?.message ?? t(PREVIEW_SAVE_BLOCKED_KEY), undefined, {
      label: cover?.actionLabel ?? t('versionPreview.close'),
      onClick: onClosePreview,
    })
  }, [toast, clear, t])
}
